/**
 * Calls into the legacy host page (`crm/index.html`).
 *
 * MIGRATION_DEBT: the shell drives the legacy panels through the host's
 * global functions. Each helper is a no-op when the function is missing
 * (unit tests, or the host changed), so the shell never throws.
 */

import { SETTINGS_TAB } from './screens'

type LegacyFn = (...args: unknown[]) => unknown

function legacy(name: string): LegacyFn | null {
  const fn = (window as unknown as Record<string, unknown>)[name]
  return typeof fn === 'function' ? (fn as LegacyFn) : null
}

function call(name: string, ...args: unknown[]): boolean {
  const fn = legacy(name)
  if (!fn) return false
  try {
    fn(...args)
  } catch (e) {
    console.warn(`[app-shell] ${name} failed`, e)
  }
  return true
}

/** Active legacy tab, read from `.nav-tab.active` (defaults to today). */
export function readActiveTab(): string {
  const el = document.querySelector('.nav-tab.active') as HTMLElement | null
  return el?.dataset.tab ?? 'today'
}

/** Show a legacy tab. Settings goes through `openSettings()`. */
export function goToTab(tab: string): void {
  if (tab === SETTINGS_TAB) {
    call('openSettings')
    return
  }
  const el = document.querySelector(`.nav-tab[data-tab="${tab}"]`) as HTMLElement | null
  if (!el) return
  call('switchTab', el)
}

export function openSettings(section?: string): void {
  call('openSettings')
  if (section) {
    // Let the panel paint before scrolling to the section.
    setTimeout(() => call('goToSettingsSection', section), 60)
  }
}

export function newContact(): void {
  goToTab('contacts')
  setTimeout(() => call('openAddContact'), 80)
}

export function newDeal(): void {
  goToTab('pipeline')
  setTimeout(() => call('openAddDeal'), 80)
}

export function newTask(): void {
  // The task modal works from any screen; no need to leave this one.
  call('openAddTask')
}

export function importContacts(): void {
  goToTab('contacts')
  setTimeout(() => call('openImport'), 80)
}

export function openSearch(): void {
  if (call('openCmdk')) return
  window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }))
}

export function toggleTheme(): void {
  call('toggleTheme')
}

export function signOut(): void {
  call('doSignout')
}

export function openBusinesses(): void {
  call('openBusinessesPage')
}

export function currentTheme(): 'light' | 'dark' {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'
}
